import {
  TourModel,
  TourMutationInput,
} from 'api-hooks/tour/tour-list/tour-detail';
import { CircleSVG } from 'common/assets';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Tabs from 'components/common/tabs';
import { Form, FormState } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { isDataActive } from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import isEmpty from 'lodash/isEmpty';
import TitleStatus from 'legacy-modules/common/title-status';
import { FormDivider } from 'legacy-modules/order/cruise/style';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { FlightTab } from './flight/flight-form';
import { TourListFormSchema, TourListFormType } from './form-type';
import useGetDefaultValues from './hooks/use-get-default-values';
import { HotelTab } from './hotel/form';
import { ProductTab } from './product/form';
import TourTab from './tour-details';
import ActionComponent from './tour-details/action-component';

interface Props {
  tour?: TourModel;
  onSubmit: (
    input: TourMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<TourModel | undefined>;
  defaultValues?: TourListFormType;
}

export default function TourForm(props: Props) {
  const { t } = useTranslation();
  const { tour } = props;
  const [activeKey, setActiveKey] = React.useState<React.Key>('0');
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    tourList: tour,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(TourListFormSchema());

  const methods = useForm<TourListFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: TourListFormType) => {
      try {
        const tourProducts = values.tourProducts.map((tp) => ({
          ...tp,
          productVariantId: tp.productVariantId.id,
        }));

        const needApproval =
          values.transactionAt?.getTime() !==
          values.tempTransactionAt?.getTime();

        await props.onSubmit(
          {
            ...values,
            tourProducts,
            needApproval,
          },
          methods as any,
        );
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('tours');
  const isActive = isDataActive(tour?.status);
  const onRenderComponent = !!tour && (
    <ActionComponent tour={tour} defaultValues={defaultValues} />
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!tour}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={tour && `${t('common:tour')} - ${tour.code}`}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={tour}
            noDelete
            noEdit={!editable || !isActive || !permission.update}
            title={
              tour ? (
                <TitleStatus
                  status={tour.status}
                  title={`${t('common:tour')} - ${tour.code}`}
                  type="tour"
                />
              ) : (
                t('common:add_extra', { extra: t('common:tour') })
              )
            }
            onRenderRightComponent={editable ? undefined : onRenderComponent}
          />
        )}
      </FormState>
      <Tabs
        activeKey={activeKey}
        onChangeActive={({ activeKey }) => setActiveKey(activeKey)}
        items={[
          {
            title: t('common:tour_details'),
            component: <></>,
          },
          {
            title: (
              <>
                {!isEmpty(methods.formState.errors.tourFlights) && (
                  <>
                    <CircleSVG /> &nbsp;
                  </>
                )}
                {t('common:flight')}
              </>
            ),
            component: <></>,
          },
          {
            title: (
              <>
                {!isEmpty(methods.formState.errors.tourHotels) && (
                  <>
                    <CircleSVG /> &nbsp;
                  </>
                )}
                {t('common:hotel')} &nbsp;
              </>
            ),
            component: <></>,
          },
          {
            title: (
              <>
                {!isEmpty(methods.formState.errors.tourHotels) && (
                  <>
                    <CircleSVG /> &nbsp;
                  </>
                )}
                {t('common:product')} &nbsp;
              </>
            ),
            component: <></>,
          },
        ]}
        overrideStyles={tabOverrideStyle}
      />
      <div style={contentStyle}>
        {activeKey === '0' ? (
          <TourTab tour={tour} />
        ) : activeKey === '1' ? (
          <FlightTab tourFlights={tour?.tourFlights} />
        ) : activeKey === '2' ? (
          <HotelTab hotel={tour?.tourHotels} />
        ) : (
          <ProductTab products={tour?.tourProducts} />
        )}
        <FormDivider />
      </div>
    </Form>
  );
}

const tabOverrideStyle = {
  tabList: { minHeight: '0px' },
  tab: { minHeight: '0px', height: '0', flexGrow: '0' },
  tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
};

const contentStyle = { overflow: 'auto', height: '100%' };
